import React, { useEffect, useMemo } from "react";

import type { TAccessionDetail, TReport, TTestOrder } from "@/lib/types";

type ReportStatus = "preliminary" | "final" | "corrected";

type ReportContextValue = {
  accession: TAccessionDetail;
  reason?: string;
  setReason: React.Dispatch<React.SetStateAction<string | undefined>>;
  displayedTests: number[];
  setDisplayedTests: React.Dispatch<React.SetStateAction<number[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  testOrders: TTestOrder[];
  comment: string;
  setComment: React.Dispatch<React.SetStateAction<string>>;
  status: ReportStatus;
  setStatus: React.Dispatch<React.SetStateAction<ReportStatus>>;
  hasFilteredTests: boolean;
};

const ReportContext = React.createContext({} as ReportContextValue);

export function ReportProvider({
  children,
  accession,
  testOrders,
  reports,
}: {
  children: React.ReactNode;
  accession: TAccessionDetail;
  testOrders: TTestOrder[];
  reports: TReport[];
}) {
  const [reason, setReason] = React.useState<string>();
  const [loading, setLoading] = React.useState(false);
  const [comment, setComment] = React.useState<string>("");

  // const lastReport = reports[0];
  const [displayedTests, setDisplayedTests] = React.useState<number[]>(() => {
    // if (lastReport && lastReport.testOrderIds.length > 0) {
    //   return lastReport.testOrderIds;
    // }
    return testOrders.map((order) => order.id);
  });

  const initialStatus = useMemo<ReportStatus>(() => {
    if (accession.status === "final") {
      return "corrected";
    }
    if (accession.hasHold || accession.status !== "completed") {
      return "preliminary";
    }
    return "final";
  }, [accession]);

  const [status, setStatus] = React.useState<ReportStatus>(initialStatus);

  const hasFilteredTests = useMemo(() => {
    return (
      testOrders?.length !== displayedTests?.length &&
      displayedTests?.length > 0
    );
  }, [testOrders, displayedTests]);

  /**
   * If the status is corrected and a reason is entered, clear the reason when the status changes
   */
  useEffect(() => {
    if (status !== "corrected" && reason) {
      setReason(undefined);
    }
  }, [status, reason]);

  return (
    <ReportContext.Provider
      value={{
        accession,
        loading,
        setLoading,
        reason,
        setReason,
        testOrders,
        displayedTests,
        setDisplayedTests,
        comment,
        setComment,
        status,
        setStatus,
        hasFilteredTests,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
}

export function useAccessionReport() {
  return React.useContext(ReportContext);
}
